import React, { createContext, useState, useContext, useEffect } from "react";
import {
    connectToPrinter,
    disconnectPrinter,
    listUSBDevices,
    sendToPrinter,
} from "../utils/printer";
import { loadPrinterSettings, savePrinterSettings } from "../utils/storage";
import { Item, StoreDetails, DeliveryCharge, Discount } from "../types";
import {
    generateReceiptCommands,
    buildReceiptHeaderRaster,
    buildFollowFooterRaster,
} from "../utils/escpos";

// Define PrinterSettings interface here instead of importing it
interface PrinterSettings {
    defaultPrintMethod: "thermal" | "browser";
    autoConnect: boolean;
    debugMode: boolean;
}

interface PrinterContextType {
    printer: USBDevice | null;
    isConnecting: boolean;
    status: string;
    connectPrinter: () => Promise<void>;
    disconnectPrinter: () => Promise<void>;
    debugInfo: string;
    setDebugInfo: React.Dispatch<React.SetStateAction<string>>;
    debugMode: boolean;
    setDebugMode: React.Dispatch<React.SetStateAction<boolean>>;
    settings: PrinterSettings;
    updateSettings: (settings: Partial<PrinterSettings>) => void;
    printReceipt: (
        items: Item[],
        storeDetails: StoreDetails,
        deliveryCharge: DeliveryCharge,
        discount: Discount
    ) => Promise<{ success: boolean; message?: string }>;
    isPrinting: boolean;
}

const PrinterContext = createContext<PrinterContextType | undefined>(undefined);

export const usePrinter = () => {
    const context = useContext(PrinterContext);
    if (!context) {
        throw new Error("usePrinter must be used within a PrinterProvider");
    }
    return context;
};

export const PrinterProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [printer, setPrinter] = useState<USBDevice | null>(null);
    const [isConnecting, setIsConnecting] = useState<boolean>(false);
    const [status, setStatus] = useState<string>("");
    const [debugInfo, setDebugInfo] = useState<string>("");
    const [debugMode, setDebugMode] = useState<boolean>(false);
    const [isPrinting, setIsPrinting] = useState<boolean>(false);

    // Default printer settings
    const defaultSettings: PrinterSettings = {
        defaultPrintMethod: "thermal",
        autoConnect: false,
        debugMode: false,
    };

    const [settings, setSettings] = useState<PrinterSettings>(defaultSettings);

    // Load saved settings and apply them on component mount
    useEffect(() => {
        const savedSettings = loadPrinterSettings();
        if (savedSettings) {
            setSettings(savedSettings);
            setDebugMode(savedSettings.debugMode);
        }
    }, []);

    // Define connectPrinter as a useCallback to avoid dependency issues
    const connectPrinter = React.useCallback(async () => {
        try {
            setIsConnecting(true);
            setStatus("Connecting to printer...");

            // First, check for already paired devices
            if (debugMode) {
                setDebugInfo("Checking for already paired USB devices...");
                const devices = await listUSBDevices();
                setDebugInfo(
                    (prev) =>
                        `${prev}\nFound ${devices.length} already paired devices`
                );

                if (devices.length > 0) {
                    setDebugInfo(
                        (prev) =>
                            `${prev}\nDevice info: ${JSON.stringify(
                                devices.map((d) => ({
                                    vendorId: d.vendorId.toString(16),
                                    productId: d.productId.toString(16),
                                    manufacturerName: d.manufacturerName,
                                    productName: d.productName,
                                }))
                            )}`
                    );
                }
            }

            const device = await connectToPrinter();

            if (device) {
                setPrinter(device);
                setStatus("Printer connected successfully");

                if (debugMode) {
                    setDebugInfo(
                        (prev) =>
                            `${prev}\nConnected to printer:\nVendor ID: 0x${device.vendorId.toString(
                                16
                            )}\nProduct ID: 0x${device.productId.toString(
                                16
                            )}\nProduct Name: ${
                                device.productName || "Unknown"
                            }\nManufacturer: ${
                                device.manufacturerName || "Unknown"
                            }`
                    );
                }
            } else {
                setStatus("Failed to connect to printer");
            }
        } catch (error) {
            console.error("Error connecting to printer:", error);
            setStatus("Error connecting to printer");

            if (debugMode) {
                setDebugInfo(
                    (prev) =>
                        `${prev}\nError details: ${
                            error instanceof Error ? error.stack : String(error)
                        }`
                );
            }
        } finally {
            setIsConnecting(false);
        }
    }, [debugMode, setDebugInfo, setIsConnecting, setStatus, setPrinter]);

    // Handle auto-connect separately
    useEffect(() => {
        const savedSettings = loadPrinterSettings();
        if (savedSettings?.autoConnect) {
            connectPrinter().catch(console.error);
        }
    }, [connectPrinter]);

    // Clean up printer connection when component unmounts
    useEffect(() => {
        return () => {
            if (printer) {
                disconnectPrinter(printer).catch(console.error);
            }
        };
    }, [printer]);

    const disconnectPrinterHandler = async () => {
        if (!printer) return;

        try {
            const success = await disconnectPrinter(printer);

            if (success) {
                setPrinter(null);
                setStatus("Printer disconnected");
                if (debugMode) {
                    setDebugInfo(
                        (prev) => `${prev}\nPrinter disconnected successfully`
                    );
                }
            } else {
                setStatus("Failed to disconnect printer");
            }
        } catch (error) {
            console.error("Error disconnecting printer:", error);
            setStatus("Error disconnecting printer");

            if (debugMode) {
                setDebugInfo(
                    (prev) =>
                        `${prev}\nError details: ${
                            error instanceof Error ? error.stack : String(error)
                        }`
                );
            }
        }
    };

    // Update printer settings
    const updateSettings = (newSettings: Partial<PrinterSettings>) => {
        setSettings((prev) => {
            const updated = { ...prev, ...newSettings };
            savePrinterSettings(updated);
            return updated;
        });

        // Apply debug mode immediately if changed
        if (
            newSettings.debugMode !== undefined &&
            newSettings.debugMode !== debugMode
        ) {
            setDebugMode(newSettings.debugMode);
        }
    };

    // Print receipt using the appropriate method based on settings
    const printReceipt = async (
        items: Item[],
        storeDetails: StoreDetails,
        deliveryCharge: DeliveryCharge,
        discount: Discount
    ): Promise<{ success: boolean; message?: string }> => {
        if (items.length === 0) {
            return { success: false, message: "No items to print" };
        }

        // Use browser printing if that's the default or if thermal printer is not connected
        if (
            settings.defaultPrintMethod === "browser" ||
            (!printer && settings.defaultPrintMethod === "thermal")
        ) {
            try {
                window.print();
                return { success: true };
            } catch (error) {
                console.error("Error during browser printing:", error);
                return { success: false, message: "Browser print failed" };
            }
        }

        // Otherwise use thermal printer
        if (!printer) {
            // Try to connect to printer first
            try {
                setStatus("Connecting to printer...");
                await connectPrinter();
                // If connection fails, the error will be caught below
            } catch (error) {
                return {
                    success: false,
                    message: "Failed to connect to printer",
                };
            }
        }

        // Now try to print
        try {
            setIsPrinting(true);
            setStatus("Printing receipt...");

            // Build a two-column header raster (logo left small, text right)
            let headerBytes: Uint8Array | undefined = undefined;
            try {
                // totalWidth depends on printer (common: 384, 512, 576). Using 576 for many 80mm printers.
                const header = await buildReceiptHeaderRaster(
                    storeDetails,
                    "/logo512.png",
                    {
                        totalWidth: 576,
                        leftRatio: 0.22,
                        logoMaxHeight: 128,
                        nameFontSize: 48, // bigger title
                        lineFontSize: 20, // smaller details
                    }
                );
                if (header) headerBytes = header;
            } catch (e) {
                // Non-fatal: continue without header raster
                if (debugMode) {
                    setDebugInfo(
                        (prev) =>
                            `${prev}\nHeader raster build failed: ${
                                e instanceof Error ? e.message : String(e)
                            }`
                    );
                }
            }

            // Build follow footer raster (left text wider, right QR smaller)
            let footerBytes: Uint8Array | undefined = undefined;
            try {
                const footer = await buildFollowFooterRaster(
                    "Follow us on facebook or just scan the QR Code\nhttps://fb.com/catelyn.pet.shop",
                    "/cps-fb-qr-code.jpeg",
                    {
                        totalWidth: 576,
                        rightRatio: 0.35,
                        qrMaxSize: 110,
                        fontSize: 24,
                        lineGap: 4,
                        textTopPadding: 2,
                    }
                );
                if (footer) footerBytes = footer;
            } catch (e) {
                if (debugMode) {
                    setDebugInfo(
                        (prev) =>
                            `${prev}\nFooter raster build failed: ${
                                e instanceof Error ? e.message : String(e)
                            }`
                    );
                }
            }

            // Generate ESC/POS commands for the receipt, with header and footer rasters if available
            const commands = generateReceiptCommands(
                items,
                storeDetails,
                deliveryCharge,
                discount,
                headerBytes,
                footerBytes
            );

            // Send commands to printer
            await sendToPrinter(printer!, commands);

            setStatus("Receipt printed successfully");
            return { success: true };
        } catch (error) {
            console.error("Error printing receipt:", error);
            setStatus(
                `Error printing receipt: ${
                    error instanceof Error ? error.message : "Unknown error"
                }`
            );

            if (debugMode) {
                setDebugInfo(
                    (prev) =>
                        `${prev}\nPrint Error: ${
                            error instanceof Error ? error.stack : String(error)
                        }`
                );
            }

            return {
                success: false,
                message:
                    error instanceof Error
                        ? error.message
                        : "Unknown error during printing",
            };
        } finally {
            setIsPrinting(false);
        }
    };

    const value = {
        printer,
        isConnecting,
        status,
        connectPrinter,
        disconnectPrinter: disconnectPrinterHandler,
        debugInfo,
        setDebugInfo,
        debugMode,
        setDebugMode,
        settings,
        updateSettings,
        printReceipt,
        isPrinting,
    };

    return (
        <PrinterContext.Provider value={value}>
            {children}
        </PrinterContext.Provider>
    );
};
